import { useState, useRef, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Trash2Icon, XIcon } from 'lucide-react';
import { useForm, useWatch, Control, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import useDatasources from '@/hooks/useDatasources';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectDatasourceId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { DatasourceType, WebsiteConfig } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { countryListAllIsoData } from '@/util/countries';
import { extractNames, generateUniqueName, hasDirtyFields, useHasDirtyFields } from '@/util/util';

import { extractDomainName, ensureProtocol } from '../../../helper';
import { addWebpagesSchema } from '../../../validator';

type ImportMethod = 'full_website' | 'single_urls' | 'sitemap' | undefined;

// ISO-3166 alpha-2 (uppercase) codes accepted by the orchestrator proxy_country schema.
const COUNTRY_OPTIONS = countryListAllIsoData.map((country) => ({
  value: country.code,
  label: country.name,
}));

interface FormValues {
  method: ImportMethod;
  seedUrl: string;
  singleUrls: string[];
  sitemapUrl: string;
  excludedUrls: string[];
  useProxy: boolean;
  proxyCountry: string;
}

interface ExcludedUrlsProps {
  control: Control<FormValues>;
  name: 'excludedUrls';
  urls: string[];
  onAdd: () => void;
  onRemove: (index: number) => void;
}

interface SingleUrlsProps {
  control: Control<FormValues>;
  name: 'singleUrls';
  urls: string[];
  onAdd: () => void;
  onRemove: (index: number) => void;
}

// Subcomponent
const ExcludedUrls = ({ control, name, urls, onAdd, onRemove }: ExcludedUrlsProps) => {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd();
      // Focus on the next input after the state update
      setTimeout(() => {
        const nextIndex = index + 1;
        if (inputRefs.current[nextIndex]) {
          inputRefs.current[nextIndex]?.focus();
        }
      }, 0);
    }
  };

  const handleRemove = (index: number) => {
    onRemove(index);
  };

  return (
    <div className="space-y-2">
      <FormLabel>{t('collections.add_webpages.excluded_pages')}</FormLabel>

      <div className="space-y-2">
        {urls?.map((url, index) => (
          <FormField
            key={`${name}.${index}`}
            control={control}
            name={`${name}.${index}`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <div className="flex items-center gap-2">
                  <FormControl>
                    <URLInput
                      value={field.value}
                      onChange={field.onChange}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      onKeyDown={(e) => handleKeyPress(e, index)}
                    />
                  </FormControl>
                  <InfoTooltip content={t('collections.add_webpages.remove_url')} side="top">
                    <Button
                      aria-label={t('collections.add_webpages.remove_url')}
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={() => handleRemove(index)}
                    >
                      <Trash2Icon size={16} />
                    </Button>
                  </InfoTooltip>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>
    </div>
  );
};

// Subcomponent for Single URLs
const SingleUrls = ({ control, name, urls, onAdd, onRemove }: SingleUrlsProps) => {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd();
      // Focus on the next input after the state update
      setTimeout(() => {
        const nextIndex = index + 1;
        if (inputRefs.current[nextIndex]) {
          inputRefs.current[nextIndex]?.focus();
        }
      }, 0);
    }
  };

  const handleRemove = (index: number) => {
    onRemove(index);
  };

  return (
    <div className="space-y-2">
      <FormLabel>{t('collections.add_webpages.webpage_url')}</FormLabel>

      <div className="space-y-2">
        {urls?.map((url, index) => (
          <FormField
            key={`${name}.${index}`}
            control={control}
            name={`${name}.${index}`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <div className="flex items-center gap-2">
                  <FormControl>
                    <URLInput
                      value={field.value}
                      onChange={field.onChange}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      onKeyDown={(e) => handleKeyPress(e, index)}
                      autoFocus={index === 0 && canWrite}
                    />
                  </FormControl>
                  <InfoTooltip content={t('collections.add_webpages.remove_url')} side="top">
                    <Button
                      aria-label={t('collections.add_webpages.remove_url')}
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={() => handleRemove(index)}
                    >
                      <Trash2Icon size={16} />
                    </Button>
                  </InfoTooltip>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>
    </div>
  );
};

export const AddWebpages = () => {
  // Redux
  const { collectionId, datasourceId: datasourceIdFromParams } = useParams();
  const reduxDatasourceId = useSelector(selectDatasourceId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const datasourceId = datasourceIdFromParams || reduxDatasourceId;

  // Hooks
  const { createDatasource, updateDatasource, datasources, datasource, getStatus } = useDatasources(
    collectionId,
    datasourceId
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Local state
  const [excludedUrls, setExcludedUrls] = useState<string[]>(['']);
  const [singleUrls, setSingleUrls] = useState<string[]>(['']);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // Local variables
  const hasSelection = !!datasourceId && !!collectionId;
  // The type check stops a stale selection from updating a non-website datasource.
  const isEditing = hasSelection && datasource?.type === DatasourceType.WEBSITE;
  // A slow or failed load must not drop the form into create mode: hold the edit view and block submit.
  const isSelectionUnresolved = hasSelection && getStatus !== 'success';
  const isEditingOrLoading = isEditing || isSelectionUnresolved;
  // Find the datasource being edited
  const editingDatasource = datasource;

  // RHF
  const form = useForm<FormValues>({
    resolver: yupResolver(addWebpagesSchema) as Resolver<FormValues>,
    defaultValues: {
      method: 'full_website',
      seedUrl: '',
      singleUrls: [''],
      sitemapUrl: '',
      excludedUrls: [''],
      useProxy: false,
      proxyCountry: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const { control, handleSubmit, setValue, clearErrors } = form;
  const method = useWatch({ control, name: 'method' });
  const useProxy = useWatch({ control, name: 'useProxy' });

  // Load existing datasource data when editing
  useEffect(() => {
    if (isEditing && editingDatasource && editingDatasource.type === DatasourceType.WEBSITE) {
      const config = editingDatasource.config as WebsiteConfig;

      // Determine the method based on which URLs are populated
      let detectedMethod: ImportMethod = undefined;
      let seedUrl = '';
      let singleUrlsList: string[] = [''];
      let sitemapUrl = '';
      let excludedUrlsList: string[] = [''];

      if (config.seed_urls && config.seed_urls.length > 0) {
        detectedMethod = 'full_website';
        seedUrl = config.seed_urls[0] || '';
      } else if (config.single_urls && config.single_urls.length > 0) {
        detectedMethod = 'single_urls';
        singleUrlsList = config.single_urls.length > 0 ? config.single_urls : [''];
      } else if (config.sitemap_urls && config.sitemap_urls.length > 0) {
        detectedMethod = 'sitemap';
        sitemapUrl = config.sitemap_urls[0] || '';
      }

      // Handle excluded URLs
      if (config.exclude_urls && config.exclude_urls.length > 0) {
        excludedUrlsList = config.exclude_urls;
      }

      // Reset form with loaded data
      form.reset({
        method: detectedMethod,
        seedUrl,
        singleUrls: singleUrlsList,
        sitemapUrl,
        excludedUrls: excludedUrlsList,
        useProxy: config.use_proxy ?? false,
        proxyCountry: config.proxy_country ?? '',
      });

      // Update local state
      setSingleUrls(singleUrlsList);
      setExcludedUrls(excludedUrlsList);
    }
  }, [isEditing, editingDatasource, form, datasources]);

  // Clear fields and errors when method changes
  useEffect(() => {
    // Skip this effect when editing and initially loading data
    if (method && !(isEditing && editingDatasource)) {
      setValue('seedUrl', '');
      setValue('singleUrls', ['']);
      setValue('sitemapUrl', '');
      setValue('excludedUrls', ['']);
      setExcludedUrls(['']);
      setSingleUrls(['']);
      clearErrors();
    }
  }, [method, setValue, clearErrors, isEditing, editingDatasource]);

  // The held edit view has no other way to say why its submit is disabled.
  useEffect(() => {
    if (hasSelection && getStatus === 'error') {
      toast.error(t('collections.toast_messages.datasource_load_failed'));
    }
  }, [hasSelection, getStatus, t]);

  const resetReduxState = () => {
    dispatch(setDatasourceId(null));
    dispatch(setDocumentId(null));
  };

  // Handlers
  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      resetReduxState();
      dispatch(setSidebarContent(null));
    }
  };

  const addExcludedUrl = () => {
    const currentUrls = form.getValues('excludedUrls');
    setValue('excludedUrls', [...currentUrls, '']);
    setExcludedUrls([...excludedUrls, '']);
  };

  const removeExcludedUrl = (index: number) => {
    const currentUrls = form.getValues('excludedUrls');
    if (currentUrls.length === 1) {
      // If it's the last field, clear it instead of removing
      setValue('excludedUrls', ['']);
      setExcludedUrls(['']);
      clearErrors('excludedUrls');
    } else {
      const newUrls = [...currentUrls];
      newUrls.splice(index, 1);
      setValue('excludedUrls', newUrls);
      clearErrors(`excludedUrls.${index}`);
      setExcludedUrls((prev) => {
        const newState = [...prev];
        newState.splice(index, 1);
        return newState;
      });
    }
  };

  const addSingleUrl = () => {
    const currentUrls = form.getValues('singleUrls');
    setValue('singleUrls', [...currentUrls, '']);
    setSingleUrls([...singleUrls, '']);
  };

  const removeSingleUrl = (index: number) => {
    const currentUrls = form.getValues('singleUrls');
    if (currentUrls.length === 1) {
      // If it's the last field, clear it instead of removing
      setValue('singleUrls', ['']);
      setSingleUrls(['']);
      clearErrors('singleUrls');
    } else {
      const newUrls = [...currentUrls];
      newUrls.splice(index, 1);
      setValue('singleUrls', newUrls);
      clearErrors(`singleUrls.${index}`);
      setSingleUrls((prev) => {
        const newState = [...prev];
        newState.splice(index, 1);
        return newState;
      });
    }
  };

  const resetForm = () => {
    // Reset form to default values
    form.reset({
      method: 'full_website',
      seedUrl: '',
      singleUrls: [''],
      sitemapUrl: '',
      excludedUrls: [''],
      useProxy: false,
      proxyCountry: '',
    });
    // Reset local state
    setSingleUrls(['']);
    setExcludedUrls(['']);
  };

  const onSubmit = (data: FormValues) => {
    const websiteDatasources = datasources?.filter(
      (datasource) => datasource.type === DatasourceType.WEBSITE
    );

    const datasourceConfig = {
      seed_urls: method === 'full_website' ? [ensureProtocol(data.seedUrl.trim())] : [],
      single_urls:
        method === 'single_urls'
          ? data.singleUrls
              .filter((url) => url.trim() !== '')
              .map((url) => ensureProtocol(url.trim()))
          : [],
      sitemap_urls: method === 'sitemap' ? [ensureProtocol(data.sitemapUrl.trim())] : [],
      exclude_urls: data.excludedUrls
        ? data.excludedUrls
            ?.filter((url) => url.trim() !== '')
            .map((url) => ensureProtocol(url.trim()))
        : [],
      use_proxy: data.useProxy,
      // Only send proxy_country when the proxy is enabled: the orchestrator
      // forbids proxy_country unless use_proxy === true (rejects otherwise).
      ...(data.useProxy && data.proxyCountry ? { proxy_country: data.proxyCountry } : {}),
    };

    if (isEditing) {
      // Update existing datasource
      updateDatasource(
        { datasource_id: datasourceId, config: datasourceConfig, type: DatasourceType.WEBSITE },
        {
          onSuccess: () => {
            resetForm();
            resetReduxState();
            dispatch(setSidebarContent('add_webpages'));
          },
        }
      );
    } else {
      // Create new datasource
      const datasourceName =
        extractDomainName((data.seedUrl || data.singleUrls?.[0] || data.sitemapUrl).trim()) +
        ' ' +
        t('try_it.datasource_icons.website').toLowerCase();

      const newDatasource = {
        type: DatasourceType.WEBSITE,
        name: generateUniqueName(datasourceName, extractNames(websiteDatasources)),
        config: datasourceConfig,
      };

      createDatasource(newDatasource, {
        onSuccess: () => {
          const toastHeader =
            method === 'sitemap'
              ? t('collections.toast_messages.sitemap_uploaded')
              : t('collections.toast_messages.webpages_uploaded');

          toast(toastHeader, { description: t('collections.toast_messages.draft_webpages') });

          resetForm();
          resetReduxState();
          dispatch(setSidebarContent('add_webpages'));
        },
      });
    }
  };

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'add-webpages' });
  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {isEditingOrLoading
            ? t('collections.add_webpages.edit_title')
            : t('collections.add_webpages.title')}
        </h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-1">
            <h2 className="mb-4 text-sm leading-5 font-medium text-muted-foreground">
              {t('collections.add_webpages.select_method')}
            </h2>

            <FormField
              control={control}
              name="method"
              render={({ field }) => (
                <FormItem className="space-y-4">
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="space-y-4"
                    >
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="full_website" id="full_website" className="mt-1" />

                        <Label
                          htmlFor="full_website"
                          className="grid cursor-pointer gap-1 text-sm leading-5 font-medium"
                        >
                          {t('collections.add_webpages.full_website_title')}
                          <p className="text-sm leading-5 font-normal text-muted-foreground">
                            {t('collections.add_webpages.full_website_description')}
                          </p>
                        </Label>
                      </div>
                      {method === 'full_website' && (
                        <>
                          <FormField
                            control={control}
                            name="seedUrl"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('collections.add_webpages.webpage_url')}</FormLabel>
                                <FormControl>
                                  <URLInput
                                    value={field.value}
                                    onChange={field.onChange}
                                    autoFocus={canWrite}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <ExcludedUrls
                            control={control}
                            name="excludedUrls"
                            urls={excludedUrls}
                            onAdd={addExcludedUrl}
                            onRemove={removeExcludedUrl}
                          />
                        </>
                      )}

                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="single_urls" id="single_urls" className="mt-1" />

                        <Label
                          htmlFor="single_urls"
                          className="grid cursor-pointer gap-1 text-sm leading-5 font-medium"
                        >
                          {t('collections.add_webpages.single_url_title')}
                          <p className="text-sm leading-5 font-normal text-muted-foreground">
                            {t('collections.add_webpages.single_url_description')}
                          </p>
                        </Label>
                      </div>
                      {method === 'single_urls' && (
                        <>
                          <SingleUrls
                            control={control}
                            name="singleUrls"
                            urls={singleUrls}
                            onAdd={addSingleUrl}
                            onRemove={removeSingleUrl}
                          />
                        </>
                      )}

                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="sitemap" id="sitemap" className="mt-1" />

                        <Label
                          htmlFor="sitemap"
                          className="grid cursor-pointer gap-1 text-sm leading-5 font-medium"
                        >
                          {t('collections.add_webpages.sitemap_title')}
                          <p className="text-sm leading-5 font-normal text-muted-foreground">
                            {t('collections.add_webpages.sitemap_description')}
                          </p>
                        </Label>
                      </div>
                      {method === 'sitemap' && (
                        <>
                          <FormField
                            control={control}
                            name="sitemapUrl"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('collections.add_webpages.sitemap_url')}</FormLabel>
                                <FormControl>
                                  <URLInput
                                    value={field.value}
                                    onChange={field.onChange}
                                    autoFocus={canWrite}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <ExcludedUrls
                            control={control}
                            name="excludedUrls"
                            urls={excludedUrls}
                            onAdd={addExcludedUrl}
                            onRemove={removeExcludedUrl}
                          />
                        </>
                      )}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {method && (
              <div className="mt-6 space-y-4 border-t border-border pt-6">
                <FormField
                  control={control}
                  name="useProxy"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between space-y-0">
                      <FormLabel className="grid gap-1 text-sm leading-5 font-medium">
                        {t('collections.add_webpages.use_proxy_title')}
                        <span className="text-sm leading-5 font-normal text-muted-foreground">
                          {t('collections.add_webpages.use_proxy_description')}
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            // Drop any selected country when the proxy is turned
                            // off so re-enabling it doesn't resubmit a stale one.
                            if (!checked) {
                              setValue('proxyCountry', '');
                            }
                          }}
                          disabled={!canWrite}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {useProxy && (
                  <FormField
                    control={control}
                    name="proxyCountry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('collections.add_webpages.proxy_country_label')}</FormLabel>
                        <FormControl>
                          <PopoverCombobox
                            options={COUNTRY_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                            triggerPlaceholder={t(
                              'collections.add_webpages.proxy_country_placeholder'
                            )}
                            disabled={!canWrite}
                            allowDeselect
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}
          </SidebarWrapperContent>

          {method && (
            <SidebarWrapperFooter>
              <Button
                aria-label="submit-webpages-form"
                type="submit"
                disabled={isSelectionUnresolved}
                isLoading={form.formState.isSubmitting}
              >
                {isEditingOrLoading ? t('common.update') : t('common.add')}
              </Button>
              <Button type="button" variant="ghost-neutral" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </SidebarWrapperFooter>
          )}
        </SidebarWrapperForm>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            dispatch(setSidebarContent(null));
            dispatch(setDatasourceId(null));
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
